import React, { useEffect, useState } from 'react';
import {
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionBody,
    Spinner,
    Tooltip,
    UncontrolledTooltip
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import axiosPrivate from 'config/axiosPrivate';
import { animationUrl } from 'config/endpoints';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import { CustomAutocomplete } from 'components/shared/DropDown/CustomAutocomplete';
import { useHistory, useParams } from 'react-router-dom';
import { CustomAutocompleteListAndFreeSolo } from '../../components/shared/DropDown/CustomAutocompleteFreeSolo';

export default function CreateCustomAIAgentForm({
    returnToCreateProject,
    selectedWebsite,
    selectedLanguage, // <<=== NOUVEAU : vient du parent
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [openSection, setOpenSection] = useState('1');
    const toggleSection = (id) => setOpenSection(openSection === id ? '' : id);

    const { agentId } = useParams();
    const resolvedAgentId = (agentId || window.localStorage.getItem('AdminClient_AgentIA_edit_id') || '').trim();
    const history = useHistory();

    const [fileName, setFileName] = useState('Aucun fichier sélectionné');
    const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState([]);
    const [errorSuccess, setErrorSuccess] = useState(false);
    const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
    const [errorSuccessImg, setErrorSuccessImg] = useState(undefined);
    const [isSuccess, setIsSuccess] = useState(false);
    const { t } = useTranslation();

    const API = {
        CreateAgentAI: '/smart-content/agent_ia/redaction/create',
        GetAgentAI: '/smart-content/agent_ia/',
        UpdateAgentAI: (id) => `/smart-content/agent_ia/redaction/${id}/update`,
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setKnowledgeBaseFiles(files);
        setFileName(files.map((f) => f.name).join(', '));
    };

    const contentTypeList = [
        { name: 'Fiche produit', type: 'Fiche produit' },
        { name: 'GMB', type: 'GMB' },
        { name: 'Store locator', type: 'Store locator' },
        { name: 'Page categorie', type: 'Category' },
    ];

    const [formData, setFormData] = useState({
        nomAgent: '',
        objectif: '',
        nstructions: '',
        tonalite: '',
        cible: '',
        structureText: '',
        RedacRules: '',
        structureTexte: '',
        InterditExpressions: '',
        TypeProducts: '',
        recommandedLexique: '',
        conaissances: '',
        urls: '',
        contentType: '',
        selectedWebsite: selectedWebsite,
        websiteId: selectedWebsite?.id || '',
        // === Langue (non affichée) ===
        langue: selectedLanguage.language,
    });

    // Sync si le parent change de site
    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            selectedWebsite,
            websiteId: selectedWebsite?.id || '',
        }));
    }, [selectedWebsite]);

    // Sync si le parent change de langue
    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            langue: selectedLanguage.language,
        }));
    }, [selectedLanguage]);

    // Mode édition : charger les données y compris la langue si renvoyée par l'API
    useEffect(() => {
        if (!resolvedAgentId) return;
        axiosPrivate
            .get(`${API.GetAgentAI}${resolvedAgentId}`)
            .then((res) => {
                const data = res.data || {};
                setFormData((prev) => ({
                    ...prev,
                    nomAgent: data.name || '',
                    objectif: data.role || '',
                    instructions: data.instructions || '',
                    tonalite: data.tonality || '',
                    cible: data.audience || '',
                    structureTexte: data.resultStructure || '',
                    RedacRules: data.redactionRules || '',
                    recommandedLexique: data.recommendedLexicons || '',
                    InterditExpressions: data.bannedExpressions || '',
                    TypeProducts: data.productType || '',
                    contentType: data.typeContenu || '',
                    websiteId: data.websiteId || '',
                    conaissances: Array.isArray(data.knowledgeBase) ? data.knowledgeBase.join(', ') : '',
                    urls: Array.isArray(data.urls) ? data.urls.join(', ') : '',
                    // langue depuis l'API si présente, sinon on garde la valeur actuelle
                    langue: data.langue || prev.langue || '',
                }));
            })
            .catch(() => { });
    }, [resolvedAgentId]);

    const handleSubmit = () => {
        const formDataFORM = new FormData();
        // Champs requis
        formDataFORM.append('name', formData.nomAgent || '');
        formDataFORM.append('instructions', formData.instructions || '');
        formDataFORM.append('tonality', formData.tonalite || '');
        formDataFORM.append('audience', formData.cible || '');
        formDataFORM.append('productType', formData.TypeProducts || '');
        formDataFORM.append('redactionRules', formData.RedacRules || '');

        // Website ID (obligatoire)
        const websiteIdToSend = formData?.selectedWebsite?.id || formData?.websiteId || '';
        if (websiteIdToSend) {
            formDataFORM.append('websiteId', websiteIdToSend);
        } else {
            setIsSuccess(false);
            setErrorSuccessMsg(t('Website ID is required'));
            setErrorSuccess(true);
            return;
        }

        // === Langue (obligatoire côté backend) ===
        const langueToSend = formData?.langue || '';
        if (!langueToSend) {
            setIsSuccess(false);
            setErrorSuccessMsg(t('La langue est requise'));
            setErrorSuccess(true);
            return;
        }
        formDataFORM.append('langue', langueToSend);

        // Champs optionnels
        formDataFORM.append('role', formData.objectif || '');
        formDataFORM.append('resultStructure', formData.structureTexte || '');
        formDataFORM.append('recommendedLexicons', formData.recommandedLexique || '');
        formDataFORM.append('bannedExpressions', formData.InterditExpressions || '');
        formDataFORM.append('personalityTraits', '');

        // Type de contenu → mapping
        const CONTENT_TYPE_MAP = {
            'Fiche produit': 'Fiche produit',
            'Fiche produit longue': 'Fiche produit longue',
            GMB: 'GMB',
            'Store locator': 'Store locator',
            Netlinking: 'Netlinking',
            Autre: 'Autre',
            Category: 'Category',
            'Content enrichment': 'Content enrichment',
            Traduction: 'Traduction',
        };
        const typeContenuCode =
            formData.contentType && /[A-Z_]{3,}/.test(formData.contentType)
                ? formData.contentType
                : CONTENT_TYPE_MAP[formData.contentType] || '';
        formDataFORM.append('typeContenu', typeContenuCode || '');

        // Fichiers
        knowledgeBaseFiles.forEach((file) => {
            formDataFORM.append('knowledgeBaseFiles', file);
        });

        // Tableaux
        const knowledgeBaseArray =
            formData.conaissances && formData.conaissances.trim()
                ? formData.conaissances
                    .split(',')
                    .map((item) => item.trim())
                    .filter((item) => item)
                : [];
        formDataFORM.append('knowledgeBase', knowledgeBaseArray);

        const urlsArray =
            formData.urls && formData.urls.trim()
                ? formData.urls
                    .split(',')
                    .map((item) => item.trim())
                    .filter((item) => item)
                : [];
        formDataFORM.append('urls', urlsArray);

        // Requête create/update
        const isEditMode = Boolean(resolvedAgentId);
        const request = isEditMode
            ? axiosPrivate.put(API.UpdateAgentAI(resolvedAgentId), formDataFORM, {
                headers: { 'X-OpenAI-Api-Key': '', 'Content-Type': 'multipart/form-data' },
            })
            : axiosPrivate.post(API.CreateAgentAI, formDataFORM, {
                headers: { 'X-OpenAI-Api-Key': '', 'Content-Type': 'multipart/form-data' },
            });

        setIsLoading(true);
        request
            .then(() => {
                setIsSuccess(true);
                setErrorSuccessMsg(
                    `${t("L'agent")} ${formData.nomAgent} ${isEditMode ? t('a été modifié avec succès') : t('a été créé avec succès')
                    }!`
                );
                setErrorSuccessImg(animationUrl + '/01_CommandeCrée.gif');
                setErrorSuccess(true);
            })
            .catch((e) => {
                setIsSuccess(false);
                switch (e?.response?.status) {
                    case 409:
                        setErrorSuccessMsg(t("L'agent déjà existant, veuillez modifier les informations"));
                        break;
                    default:
                        setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
                        break;
                }
                setErrorSuccess(true);
            })
            .finally(() => setIsLoading(false));
    };

    const handleCancel = () => {
        setFormData({
            nomAgent: '',
            objectif: '',
            instructions: '',
            tonalite: '',
            cible: '',
            structureText: '',
            RedacRules: '',
            structureTexte: '',
            InterditExpressions: '',
            recommandedLexique: '',
            TypeProducts: '',
            conaissances: '',
            urls: '',
            contentType: '',
            // conserver les valeurs venant du parent :
            selectedWebsite,
            websiteId: selectedWebsite?.id || '',
            langue: selectedLanguage.language,
        });
        if (typeof returnToCreateProject === 'function') {
            returnToCreateProject();
        } else {
            history.goBack();
        }
    };

    const handleChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    const isEditMode = Boolean(resolvedAgentId);
    const goBack = () => {
        if (typeof returnToCreateProject === 'function') {
            returnToCreateProject();
        } else {
            history.goBack();
        }
    };

    const containerClass = !returnToCreateProject ? 'box-wrapper p-4' : 'p-4';

    return (
        <div className="p-4 max-w-3xl mx-auto">
            <div className="d-flex align-items-center mb-3">
                <span onClick={goBack} className="bt-circle me-3" style={{ cursor: 'pointer' }} />
                <h2>{isEditMode ? '🧠 Modifier un agent IA' : '🧠 Créer un agent IA'}</h2>
            </div>

            <Accordion open={openSection} toggle={toggleSection}>
                <AccordionItem>
                    <AccordionHeader targetId="1">🧩 Informations générales</AccordionHeader>
                    <AccordionBody accordionId="1">
                        <InputField
                            label="Nom de l’agent*"
                            placeholder="Ex : Agent Store locator"
                            value={formData.nomAgent}
                            onChange={(e) => handleChange('nomAgent', e.target.value)}
                        />
                        <InputField
                            label={t('Rôle*')}
                            value={formData.objectif}
                            onChange={(e) => handleChange('objectif', e.target.value)}
                            placeholder={t('Ex: Rédacteur SEO')}
                        />
                        <div>
                            <div className="label-title">{t('Type de contenu')}</div>
                            <CustomAutocompleteListAndFreeSolo
                                disabled={false}
                                list={contentTypeList}
                                value={formData.contentType}
                                showDropdownIcon={true}
                                setValue={(val) => {
                                    if (typeof val === 'string') {
                                        handleChange('contentType', val);
                                    } else if (val && typeof val === 'object') {
                                        handleChange('contentType', val.type);
                                    } else {
                                        handleChange('contentType', '');
                                    }
                                }}
                                getOptionLabel={(option) => option?.name || ''}
                                onInputChange={(text) => handleChange('contentType', text)}
                            />
                        </div>
                    </AccordionBody>
                </AccordionItem>

                <AccordionItem>
                    <AccordionHeader targetId="2">🗣️ Ton & Audience</AccordionHeader>
                    <AccordionBody accordionId="2">
                        <div className="mb-3">
                            <div className="label-title">Tonalité*</div>
                            <CustomAutocomplete
                                disabled={false}
                                list={[
                                    { id: 1, name: 'Expert' },
                                    { id: 11, name: 'Professionnel' },
                                    { id: 2, name: 'Technique' },
                                    { id: 3, name: 'Sérieux' },
                                    { id: 4, name: 'Amical' },
                                    { id: 5, name: 'Convivial' },
                                    { id: 6, name: 'Persuasif' },
                                    { id: 7, name: 'Dynamique' },
                                    { id: 8, name: 'Humoristique' },
                                    { id: 9, name: 'Rassurant' },
                                    { id: 10, name: 'Bienveillant' },
                                    { id: 12, name: 'Commercial' },
                                ]}
                                value={formData.tonalite}
                                setValue={(option) => setFormData((prev) => ({ ...prev, tonalite: option ? option.name : '' }))}
                                groupBy={() => false}
                                getOptionSelected={(option) => option.name === formData.tonalite}
                                getOptionDisabled={() => false}
                                getOptionLabel={(option) => option?.name || ''}
                            />
                        </div>

                        <div className="mb-3">
                            <div className="label-title">Cible / Audience*</div>
                            <CustomAutocompleteListAndFreeSolo
                                list={[
                                    { name: 'Grand public' },
                                    { name: 'Professionnels / B2B' },
                                    { name: 'Jeunes adultes (18–25 ans)' },
                                    { name: 'Adultes (25–40 ans)' },
                                    { name: 'Seniors (60+)' },
                                    { name: 'Parents' },
                                    { name: 'Étudiants / Universitaires' },
                                    { name: 'Entreprises / Startups' },
                                    { name: 'Influenceurs / Créateurs de contenu' },
                                    { name: 'Utilisateurs débutants / novices' },
                                    { name: 'Experts / professionnels du secteur' },
                                    { name: 'Clients fidèles' },
                                    { name: 'Voyageurs / touristes' },
                                    { name: 'Passionnés de mode / lifestyle' },
                                    { name: 'Amateurs de technologie / geek' },
                                ]}
                                value={formData.cible}
                                showDropdownIcon={true}
                                setValue={(val) => {
                                    if (typeof val === 'string') {
                                        handleChange('cible', val);
                                    } else if (val && typeof val === 'object') {
                                        handleChange('cible', val.name);
                                    } else {
                                        handleChange('cible', '');
                                    }
                                }}
                                getOptionLabel={(option) => option?.name || ''}
                                onInputChange={(text) => handleChange('cible', text)}
                            />
                        </div>
                        <InputField
                            label="Type de produits / servies proposés par le site*"
                            value={formData.TypeProducts}
                            onChange={(e) => handleChange('TypeProducts', e.target.value)}
                            placeholder="Ex: prêt-à-porter féminin"
                        />
                    </AccordionBody>
                </AccordionItem>

                <AccordionItem>
                    <AccordionHeader targetId="3">📝 Structure & Rédaction</AccordionHeader>
                    <AccordionBody accordionId="3">
                        <TextArea
                            label="Règles rédactionnelles"
                            value={formData.RedacRules}
                            onChange={(e) => handleChange('RedacRules', e.target.value)}
                            placeholder="Ex: ne pas parler de bio, ne pas parler de sans paraben..."
                        />
                        <TextArea
                            label="Lexique recommandé"
                            value={formData.recommandedLexique}
                            onChange={(e) => handleChange('recommandedLexique', e.target.value)}
                            placeholder={'Ex : AMF (Autorité des Marchés Financiers).'}
                        />
                        <TextArea
                            label="Expressions interdites"
                            value={formData.InterditExpressions}
                            onChange={(e) => handleChange('InterditExpressions', e.target.value)}
                            placeholder={'Ex: bio, écologique, nom des concurrents'}
                        />
                        <div className="mb-3">
                            <div className="label-title">
                                {t('Structure de texte attendu')}
                                <span id="structure" className="ms-2" style={{ cursor: 'pointer', color: '#ef4444' }}>
                                    ⓘ
                                </span>
                            </div>
                            <textarea
                                className="form-control form-control-valid form-ct"
                                placeholder="Ex: un paragraphe sur les types d'intervention, un paragraphe sur les avantages"
                                value={formData.structureTexte}
                                onChange={(e) => handleChange('structureTexte', e.target.value)}
                                maxlength="1000000"
                            />
                            <UncontrolledTooltip placement="right" target="structure" className="custom-redac-tooltip">
                                {t(
                                    "Détailler le plus possible la structure attendue à l'aide d'axes de rédaction pour guider l'IA sur les sujets à traiter et dans quel ordre"
                                )}
                            </UncontrolledTooltip>
                        </div>

                        <div className="mb-3">
                            <div className="label-title">
                                Instructions de personnalisation{' '}
                                <span id="instructionredactooltip" className="ms-2" style={{ cursor: 'pointer', color: '#ef4444' }}>
                                    ⓘ
                                </span>
                            </div>
                            <textarea
                                className="form-control form-control-valid form-ct"
                                placeholder="Ex: ne traduis pas de façon littérale."
                                value={formData.instructions}
                                onChange={(e) => handleChange('instructions', e.target.value)}
                                maxLength={500}
                            />
                            <UncontrolledTooltip
                                placement="right"
                                target="instructionredactooltip"
                                className="custom-redac-tooltip"
                            >
                                {t("Ajouter des éléments que l'IA doit ou ne pas faire ou mentionner")}
                            </UncontrolledTooltip>
                        </div>
                    </AccordionBody>
                </AccordionItem>

                <AccordionItem>
                    <AccordionHeader targetId="4">📚 Base de connaissances</AccordionHeader>
                    <AccordionBody accordionId="4">
                        <div className="mb-3">
                            <div className="label-title">Base de connaissance locale</div>
                            <div
                                className="upload-file__controls form-control form-control-valid form-ct d-flex align-items-center w-100"
                                style={{ padding: '0', borderRadius: '6px' }}
                            >
                                <input type="file" id="upload-file__input" multiple style={{ display: 'none' }} onChange={handleFileChange} />
                                <label
                                    htmlFor="upload-file__input"
                                    className="upload-file__browse-btn"
                                    style={{
                                        backgroundColor: '#ffe426',
                                        border: 'none',
                                        color: '#000',
                                        borderTopLeftRadius: '6px',
                                        borderBottomLeftRadius: '6px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        fontWeight: 350,
                                        height: '100%',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    Sélect. fichiers
                                </label>
                                <span className="upload-file__status" style={{ color: '#6c757d', paddingLeft: '12px' }}>
                                    {fileName}
                                </span>
                            </div>
                        </div>
                        <div className="mb-3">
                            <div className="label-title">
                                Base de connaissance en ligne{' '}
                                <span id="connaissancetrad" className="ms-2" style={{ cursor: 'pointer', color: '#ef4444' }}>
                                    ⓘ
                                </span>
                            </div>
                            <textarea
                                className="form-control form-control-valid form-ct"
                                placeholder="Ex: URL1, URL2"
                                value={formData.urls}
                                onChange={(e) => handleChange('urls', e.target.value)}
                                maxLength={500}
                            />
                            <UncontrolledTooltip placement="right" target="connaissancetrad" className="custom-redac-tooltip">
                                {t("Indiquer la liste des urls sur lesquels l'IA peut s'appuyer")}
                            </UncontrolledTooltip>
                        </div>
                    </AccordionBody>
                </AccordionItem>
            </Accordion>

            <div className="text-center mt-4">
                <button className="bt-cancel me-3" onClick={handleCancel}>
                    Annuler
                </button>
                <button className="bt-submit-y" disabled={isLoading} onClick={handleSubmit}>
                    {isLoading ? <Spinner size="sm" /> : isEditMode ? 'Mettre à jour' : 'Créer l’agent'}
                </button>
            </div>

            <ErrorSuccesModal
                open={errorSuccess}
                message={errorSuccessMsg}
                handleClose={() => (isSuccess ? goBack() : setErrorSuccess(false))}
                success={isSuccess}
                toggle={() => setErrorSuccess(!errorSuccess)}
                imageUrl={animationUrl + '/01_CommandeCrée.gif'}
            />
        </div>
    );
}

/* --- SOUS-COMPOSANTS --- */
function InputField({ label, value, onChange, placeholder }) {
    return (
        <div className="mb-3">
            <label className="label-title">{label}</label>
            <input type="text" value={value} onChange={onChange} placeholder={placeholder} className="form-control form-ct" />
        </div>
    );
}

function TextArea({ label, value, onChange, placeholder }) {
    return (
        <div className="mb-3">
            <label className="label-title">{label}</label>
            <textarea value={value} onChange={onChange} placeholder={placeholder} className="form-control form-ct" rows={3} />
        </div>
    );
}
